import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "tabButton",
    "tabPanel",
    "recipientSelector",
    "audienceTab",
    "allStudentsOption",
    "zoneValue",
    "classroomValue",
    "studentProgramValue",
    "pupilValue",
    "studentProgramStatusPanel",
    "makeupStudentsPanel",
    "postcodeFilterPanel",
    "recipientError"
  ]

  connect() {
    this.syncRecipientSelector()
    this.toggleDependentPanels()
  }

  selectTab(event) {
    event.preventDefault()
    this.showTab(event.currentTarget.dataset.tab)
  }

  showTab(tab) {
    this.tabButtonTargets.forEach((button) => {
      button.classList.toggle("is-active", button.dataset.tab === tab)
    })

    if (this.hasAudienceTabTarget) {
      this.audienceTabTarget.value = this.currentTab()
    }

    this.tabPanelTargets.forEach((panel) => {
      panel.classList.toggle("u-hidden", panel.dataset.tab !== tab)
    })

    this.syncRecipientSelector()
    this.toggleDependentPanels()
    this.clearError()
  }

  onSelectorChanged() {
    this.syncRecipientSelector()
    this.toggleDependentPanels()
    this.clearError()
  }

  updateInstructorSelection() {
    this.onSelectorChanged()
  }

  syncRecipientSelector() {
    if (!this.hasRecipientSelectorTarget) return

    const tab = this.currentTab()
    let value = ""

    if (tab === "all_students") {
      const selected = this.allStudentsOptionTargets.find((option) => option.checked)
      value = selected ? selected.value : ""
    } else if (tab === "zones") {
      value = this.normalizedValue(this.hasZoneValueTarget ? this.zoneValueTarget.value : "")
    } else if (tab === "classrooms") {
      value = this.normalizedValue(this.hasClassroomValueTarget ? this.classroomValueTarget.value : "")
    } else if (tab === "student_programs") {
      value = this.normalizedValue(this.hasStudentProgramValueTarget ? this.studentProgramValueTarget.value : "")
    } else if (tab === "individual") {
      const pupilId = this.normalizedValue(this.hasPupilValueTarget ? this.pupilValueTarget.value : "")
      value = pupilId ? `pupil_${pupilId}` : ""
    }

    this.recipientSelectorTarget.value = value
  }

  toggleDependentPanels() {
    const tab = this.currentTab()
    const recipientId = this.hasRecipientSelectorTarget ? this.recipientSelectorTarget.value : ""

    if (this.hasStudentProgramStatusPanelTarget) {
      const show = tab === "student_programs" && String(recipientId).startsWith("student_program_")
      this.studentProgramStatusPanelTarget.classList.toggle("u-hidden", !show)
    }

    if (this.hasMakeupStudentsPanelTarget) {
      const show = tab === "classrooms" && String(recipientId).startsWith("program_")
      this.makeupStudentsPanelTarget.classList.toggle("u-hidden", !show)
    }

    if (this.hasPostcodeFilterPanelTarget) {
      this.postcodeFilterPanelTarget.classList.toggle("u-hidden", tab === "individual")
    }
  }

  validateSubmit(event) {
    this.syncRecipientSelector()
    const tab = this.currentTab()
    const value = this.hasRecipientSelectorTarget ? this.recipientSelectorTarget.value : ""
    const needsSelection = ["zones", "classrooms", "student_programs", "individual"]

    if (needsSelection.includes(tab) && !value) {
      event.preventDefault()
      this.showError(this.selectionErrorFor(tab))
    }
  }

  currentTab() {
    const activeButton = this.tabButtonTargets.find((button) => button.classList.contains("is-active"))
    return activeButton?.dataset.tab || "all_students"
  }

  normalizedValue(value) {
    if (value == null) return ""
    const trimmed = String(value).trim()
    if (!trimmed || trimmed === "0") return ""
    return trimmed
  }

  selectionErrorFor(tab) {
    switch (tab) {
      case "zones":
        return "Please select a zone."
      case "classrooms":
        return "Please select a classroom."
      case "student_programs":
        return "Please select a student program."
      case "individual":
        return "Please select a student."
      default:
        return "Please select who to send this message to."
    }
  }

  showError(message) {
    if (!this.hasRecipientErrorTarget) return
    this.recipientErrorTarget.textContent = message
    this.recipientErrorTarget.classList.remove("u-hidden")
  }

  clearError() {
    if (!this.hasRecipientErrorTarget) return
    this.recipientErrorTarget.textContent = ""
    this.recipientErrorTarget.classList.add("u-hidden")
  }
}
